import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Switch,
  Button,
  Dropdown,
  Menu,
  Table,
  Typography,
  message,
} from 'antd';
import {
  FolderOutlined,
  PlusOutlined,
  SendOutlined,
  CommentOutlined,
  PlusCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectAuth } from '@/redux/auth/selectors';
import AutoCompleteAsync from '@/components/AutoCompleteAsync';
import ActionMultipleAutoCompleteAsync from '@/forms/WorkstreamActionAutoCompleteAsync';
import { getObfuscatedPath } from '@/utils/apiRoute';
import { makeApiRequest } from '@/components/Kanban/request/apiRequest';
import { useHistory } from 'react-router-dom';

const { Option } = Select;
const { TextArea } = Input;

// Memoized Comment Item Component
const MemoizedCommentItem = memo(({ commentObj }) => (
  <div
    style={{
      marginBottom: '4px',
      padding: '8px',
      border: '1px solid #ddd',
      borderRadius: '4px',
    }}
  >
    <p>
      <CommentOutlined
        style={{
          backgroundColor: 'lightblue',
          padding: '2px',
          borderRadius: '3px',
          marginRight: '5px',
        }}
      />{' '}
      <strong>
        {commentObj.commentedBy?.firstName} {commentObj.commentedBy?.lastName}
      </strong>
      <br />
      {commentObj.comment}
      <br />
    </p>
    <p style={{ fontSize: '0.8em', color: '#888' }}>
      {moment(commentObj.commentedDate).format('YYYY-MM-DD HH:mm')}
    </p>
  </div>
));

// Memoized Activity Item Component
const MemoizedActivityItem = memo(({ activity, getIcon, showChangeDetails }) => (
  <div
    style={{
      marginBottom: '4px',
      padding: '8px',
      border: '1px solid #ddd',
      borderRadius: '4px',
    }}
  >
    <p>
      {getIcon(activity.event)}{' '}
      <strong>
        {activity.owner?.firstName} {activity.owner?.lastName}
      </strong>{' '}
      <br />
      {activity.event}
      {activity.changes && Object.keys(activity.changes).length > 0 && (
        <div style={{ marginLeft: '20px' }}>
          {Object.entries(activity.changes).map(([key, change], changeIndex) => {
            if (key === 'comments') return null;
            return (
              <Typography.Link
                key={changeIndex}
                onClick={() => showChangeDetails(change)}
              >
                {key}
                {changeIndex < Object.entries(activity.changes).length - 1 ? ', ' : ''}
              </Typography.Link>
            );
          })}
        </div>
      )}
    </p>
    <p style={{ fontSize: '0.8em', color: '#888' }}>
      {moment(activity.eventDate).format('YYYY-MM-DD HH:mm')}
    </p>
  </div>
));

const WorkstreamActionModal = ({
  isWorkstreamActionModalOpen,
  setWorkstreamActionModal,
  workstreamActionDetails = null,
  users = [],
  onWorkstreamActionUpdate,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [selectedActivityType, setSelectedActivityType] = useState('both');
  const [changeDetails, setChangeDetails] = useState(null);
  const [changeModalVisible, setChangeModalVisible] = useState(false);

  const commentsActivitiesRef = useRef(null);
  const history = useHistory();

  const currentUser = useSelector(selectAuth);
  const hasSpecialAccess = currentUser?.hasSpecialAccess === true;
  const isCTORole = currentUser?.role?.name === 'CTO';

  const isCTO = isCTORole || hasSpecialAccess;

  const allowToEdit = isCTO;
  const obfucatePath = getObfuscatedPath('user');

  // Helper function to get user name from either ID string or user object
  const getUserName = useCallback((userValue) => {
    if (!userValue) return null;
    
    // If it's already an object with firstName and lastName
    if (typeof userValue === 'object' && userValue.firstName) {
      return `${userValue.firstName} ${userValue.lastName || ''}`.trim();
    }
    
    // If it's a string ID, find the user from users array
    if (typeof userValue === 'string' && users) {
      const user = users.find((u) => u._id === userValue);
      if (user) {
        return `${user.firstName} ${user.lastName}`.trim();
      }
    }
    
    return null;
  }, [users]);

  // Helper function to format change value (handles both ID strings and user objects)
  const formatChangeValue = useCallback((value) => {
    if (!value) return '';
    
    // Handle single user (string ID or object)
    if (typeof value === 'string') {
      const userName = getUserName(value);
      if (userName) return userName;
      return value;
    }
    
    if (typeof value === 'object') {
      // Check if it's a user object
      if (value.firstName || value.lastName) {
        return `${value.firstName || ''} ${value.lastName || ''}`.trim();
      }
      
      // Handle array of users
      if (Array.isArray(value)) {
        return value
          .map((item) => {
            if (typeof item === 'string') {
              const userName = getUserName(item);
              return userName || item;
            }
            if (item && typeof item === 'object') {
              if (item.firstName || item.lastName) {
                return `${item.firstName || ''} ${item.lastName || ''}`.trim();
              }
              return JSON.stringify(item);
            }
            return item;
          })
          .join(', ');
      }
      
      // Handle other objects
      return JSON.stringify(value);
    }
    
    return String(value);
  }, [getUserName]);

  // Format owner name function
  const formatOwnerName = useCallback((owner) => {
    if (!owner) return '';
    
    if (typeof owner === 'object' && owner.firstName && owner.lastName) {
      return `${owner.firstName} ${owner.lastName}`;
    }
    
    if (typeof owner === 'string') {
      const userName = getUserName(owner);
      if (userName) return userName;
    }
    
    return owner;
  }, [getUserName]);

  // Get owner ID for form value
  const getOwnerId = useCallback(() => {
    if (!workstreamActionDetails?.owner) return undefined;

    if (typeof workstreamActionDetails.owner === 'object') {
      return workstreamActionDetails.owner._id;
    }

    return workstreamActionDetails.owner;
  }, [workstreamActionDetails]);

  // Get owner display value for AutoCompleteAsync
  const getOwnerDisplayValue = useCallback(() => {
    if (!workstreamActionDetails?.owner) return undefined;

    if (typeof workstreamActionDetails.owner === 'object') {
      return {
        _id: workstreamActionDetails.owner._id,
        firstName: workstreamActionDetails.owner.firstName,
        lastName: workstreamActionDetails.owner.lastName
      };
    }

    // If owner is just an ID, find the user object
    if (users && users.length > 0) {
      const user = users.find(u => u._id === workstreamActionDetails.owner);
      if (user) {
        return {
          _id: user._id,
          firstName: user.firstName,
          lastName: user.lastName
        };
      }
    }

    return undefined;
  }, [workstreamActionDetails, users]);

  // Get collaborator values for form
  const getCollaboratorValues = useCallback(() => {
    if (!workstreamActionDetails?.collaborator || !Array.isArray(workstreamActionDetails.collaborator)) {
      return [];
    }

    // If collaborators are objects, extract their IDs
    if (workstreamActionDetails.collaborator.length > 0 &&
      typeof workstreamActionDetails.collaborator[0] === 'object') {
      return workstreamActionDetails.collaborator.map(collab => collab._id);
    }

    return workstreamActionDetails.collaborator;
  }, [workstreamActionDetails]);

  // Get collaborator display values for AutoCompleteAsync
  const getCollaboratorDisplayValues = useCallback(() => {
    if (!workstreamActionDetails?.collaborator || !Array.isArray(workstreamActionDetails.collaborator)) {
      return [];
    }

    // If collaborators are already objects with firstName/lastName, return them
    if (workstreamActionDetails.collaborator.length > 0 &&
      workstreamActionDetails.collaborator[0].firstName) {
      return workstreamActionDetails.collaborator.map(collab => ({
        _id: collab._id,
        firstName: collab.firstName,
        lastName: collab.lastName
      }));
    }

    // If collaborators are IDs, find the user objects
    if (users && users.length > 0) {
      return workstreamActionDetails.collaborator
        .map(collabId => {
          const user = users.find(u => u._id === collabId);
          return user ? {
            _id: user._id,
            firstName: user.firstName,
            lastName: user.lastName
          } : null;
        })
        .filter(Boolean);
    }

    return [];
  }, [workstreamActionDetails, users]);

  // Add Menu for modal header
  const handleAddNewInitiative = useCallback(() => {
    history.push('/initiative');
  }, [history]);

  const handleAddNewWorkstreamAction = useCallback(() => {
    history.push('/workstreamaction');
  }, [history]);

  const addMenu = (
    <Menu style={{ background: '#253271', color: 'white' }}>
      <Menu.Item key="add-initiative" onClick={handleAddNewInitiative} style={{ color: 'white' }}>
        Add New Initiative
      </Menu.Item>
      <Menu.Item
        key="add-workstream-action"
        onClick={handleAddNewWorkstreamAction}
        style={{ color: 'white' }}
      >
        Add New Workstream Action
      </Menu.Item>
    </Menu>
  );

  const handleCopyLinkClick = useCallback(() => {
    const currentUrl = window.location.href;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(currentUrl)
        .then(() => {
          alert('Link copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy link: ', err);
          fallbackCopyToClipboard(currentUrl);
        });
    } else {
      fallbackCopyToClipboard(currentUrl);
    }
  }, []);

  const fallbackCopyToClipboard = useCallback((text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      const msg = successful ? 'Link copied to clipboard!' : 'Failed to copy link';
      alert(msg);
    } catch (err) {
      console.error('Fallback: Failed to copy link: ', err);
      alert('Failed to copy link. Please try manually.');
    }

    document.body.removeChild(textArea);
  }, []);

  useEffect(() => {
    if (isWorkstreamActionModalOpen && workstreamActionDetails) {
      // prepare comments
      const combinedComments = [];
      if (workstreamActionDetails.comment) {
        combinedComments.push({
          comment: workstreamActionDetails.comment,
          commentedBy: workstreamActionDetails.createdBy,
          commentedDate: workstreamActionDetails.created,
        });
      }
      if (Array.isArray(workstreamActionDetails.comments)) {
        workstreamActionDetails.comments.forEach((c) => {
          combinedComments.push({
            comment: c.comment,
            commentedBy: c.commentedBy,
            commentedDate: c.commentedDate,
          });
        });
      }
      // Sort comments in descending order (newest first)
      const sortedComments = combinedComments.sort((a, b) =>
        new Date(b.commentedDate) - new Date(a.commentedDate)
      );
      setComments(sortedComments);

      // Set form values
      const ownerValue = getOwnerDisplayValue();
      const collaboratorValue = getCollaboratorDisplayValues();

      setTimeout(() => {
        form.setFieldsValue({
          workstreamActionName: workstreamActionDetails.workstreamActionName || '',
          owner: ownerValue,
          collaborator: collaboratorValue,
          selectedForToMeeting: workstreamActionDetails.selectedForToMeeting || false,
          status: workstreamActionDetails.status || 'Not Completed',
          dueDate: workstreamActionDetails.dueDate
            ? moment(workstreamActionDetails.dueDate)
            : null,
          comment: workstreamActionDetails.comment || '',
        });
      }, 0);
    } else if (!isWorkstreamActionModalOpen) {
      form.resetFields();
      setComments([]);
      setNewComment('');
      setChangeDetails(null);
      setChangeModalVisible(false);
    }
  }, [isWorkstreamActionModalOpen, workstreamActionDetails, form, getOwnerDisplayValue, getCollaboratorDisplayValues]);

  // Field update functions
  const handleSaveField = useCallback(async (fieldName, fieldValue) => {
    if (!workstreamActionDetails) return;

    setSaving(true);
    const requestData = {
      [fieldName]: fieldValue,
    };

    try {
      const res = await makeApiRequest(
        'patch',
        `/workstreamaction/update/${workstreamActionDetails._id}`,
        requestData,
        'Field updated successfully',
        'Failed to update field'
      );

      if (res && res.success) {
        // Call the update callback if provided
        if (onWorkstreamActionUpdate) {
          onWorkstreamActionUpdate({ ...workstreamActionDetails, [fieldName]: fieldValue });
        }
        message.success('Field updated successfully');
      }
    } catch (error) {
      message.error('Failed to update field');
    } finally {
      setSaving(false);
    }
  }, [workstreamActionDetails, onWorkstreamActionUpdate]);

  const handleStatusChange = useCallback((value) => {
    handleSaveField('status', value);
  }, [handleSaveField]);

  const handleDueDateChange = useCallback((date) => {
    handleSaveField('dueDate', date);
  }, [handleSaveField]);

  const handleBlur = useCallback(async (fieldName, fieldValue) => {
    await handleSaveField(fieldName, fieldValue);
  }, [handleSaveField]);

  const handleAddComment = useCallback(async () => {
    if (!workstreamActionDetails || !newComment.trim()) return;

    const newCommentObj = {
      comment: newComment,
      commentedBy: currentUser,
      commentedDate: new Date(),
    };

    const updatedComments = [newCommentObj, ...comments];

    try {
      const requestData = {
        comments: updatedComments.map((c) => ({
          comment: c.comment,
          commentedBy: currentUser.id,
          commentedDate: c.commentedDate,
        })),
      };

      const res = await makeApiRequest(
        'put',
        `/workstreamaction/update/${workstreamActionDetails._id}`,
        requestData,
        'Comment created successfully',
        'Failed to create Comment'
      );

      if (res && res.success) {
        setComments(updatedComments);
        setNewComment('');
        // Update parent component if callback provided
        if (onWorkstreamActionUpdate) {
          onWorkstreamActionUpdate({
            ...workstreamActionDetails,
            comments: updatedComments
          });
        }
      }
    } catch (error) {
      console.error('Error updating comments:', error);
    }
  }, [newComment, comments, currentUser, workstreamActionDetails, onWorkstreamActionUpdate]);

  // Activity and change details functions
  const getIcon = useCallback((event) => {
    if (event.toLowerCase().includes('created')) {
      return <PlusCircleOutlined />;
    } else if (event.toLowerCase().includes('updated')) {
      return <SyncOutlined />;
    }
    return null;
  }, []);

  const showChangeDetails = useCallback((changes) => {
    setChangeDetails(changes);
    setChangeModalVisible(true);
  }, []);

  const handleCloseChangeModal = useCallback(() => {
    setChangeDetails(null);
    setChangeModalVisible(false);
  }, []);

  const formatCollaboratorNames = useCallback((collaboratorIds) => {
    if (!collaboratorIds || !Array.isArray(collaboratorIds) || !users) {
      return '';
    }

    return collaboratorIds
      .map((id) => {
        const user = users.find((u) => u._id === id);
        return user ? `${user.firstName} ${user.lastName}` : id;
      })
      .join(', ');
  }, [users]);

  const getChangeDetailsData = useCallback((changeDetailsData) => {
    if (!changeDetailsData) return [];

    if (changeDetailsData.collaborator) {
      return [
        {
          key: '1',
          old: formatCollaboratorNames(changeDetailsData.collaborator.old),
          new: formatCollaboratorNames(changeDetailsData.collaborator.new),
        },
      ];
    }

    return [
      {
        key: '1',
        old: formatChangeValue(changeDetailsData.old),
        new: formatChangeValue(changeDetailsData.new),
      },
    ];
  }, [formatCollaboratorNames, formatChangeValue]);

  const changesColumns = [
    {
      title: 'Old Value',
      dataIndex: 'old',
      key: 'old',
    },
    {
      title: 'New Value',
      dataIndex: 'new',
      key: 'new',
    },
  ];

  const handleModalClose = useCallback(() => {
    setWorkstreamActionModal(false);
  }, [setWorkstreamActionModal]);

  // Sort activities in descending order (newest first)
  const sortedActivities = useMemo(() => {
    if (!workstreamActionDetails?.activity) return [];
    return [...workstreamActionDetails.activity].sort((a, b) =>
      new Date(b.eventDate) - new Date(a.eventDate)
    );
  }, [workstreamActionDetails?.activity]);

  return (
    <>
      <Modal
        title={
          <div
            style={{
              backgroundColor: '#253271',
              color: 'white',
              height: '40px',
              marginTop: '-18px',
              width: '100%',
              marginLeft: '-25px',
              marginBottom: '-100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <FolderOutlined />
              <span style={{ marginLeft: '8px' }}>
                Workstream Action #{workstreamActionDetails?.actionItemNumber}
              </span>
            </span>
            <span
              style={{
                position: 'absolute',
                right: '20px',
                display: 'flex',
                gap: '10px',
              }}
            >
              <Dropdown overlay={addMenu} trigger={['click']}>
                <PlusOutlined />
              </Dropdown>
              <SendOutlined onClick={handleCopyLinkClick} />
            </span>
          </div>
        }
        bodyStyle={{
          height: '670px',
          overflowY: 'auto',
        }}
        visible={isWorkstreamActionModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width="100%"
        style={{
          top: 20,
          height: '600px'
        }}
      >
        <div style={{ display: 'flex', width: '100%' }}>
          {/* Left Side: Form */}
          <div style={{ width: '80%', paddingRight: '16px' }}>
            <Form form={form} layout="vertical">
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Workstream Action Description" name="workstreamActionName">
                    <TextArea
                      onBlur={(e) => handleBlur('workstreamActionName', e.target.value)}
                      disabled={!allowToEdit}
                      style={{
                        fontSize: '18px',
                        backgroundColor: '#fafafa',
                        color: 'rgba(0, 0, 0, 0.85)',
                        cursor: 'text',
                        borderColor: '#d9d9d9'
                      }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Owner"
                    name="owner"
                    rules={[{ required: true, message: 'Please select User !' }]}
                  >
                    <AutoCompleteAsync
                      entity={obfucatePath}
                      displayLabels={['firstName', 'lastName']}
                      searchFields={['firstName', 'lastName']}
                      handleSave={handleSaveField}
                      disabled={!allowToEdit}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Selected for TO Meeting Discussion"
                    name="selectedForToMeeting"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                      disabled={!allowToEdit}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Collaborator"
                    name="collaborator"
                    rules={[{ required: false, message: 'Please select User !' }]}
                  >
                    <ActionMultipleAutoCompleteAsync
                      entity={obfucatePath}
                      displayLabels={['firstName', 'lastName']}
                      searchFields={['firstName', 'lastName']}
                      handleSave={handleSaveField}
                      disabled={!allowToEdit}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="dueDate" label="Due Date">
                    <DatePicker
                      style={{ width: '100%' }}
                      showTime
                      onChange={handleDueDateChange}
                      disabled={!allowToEdit}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="status"
                    label="Status"
                    rules={[{ required: true, message: 'Please select Status!' }]}
                  >
                    <Select
                      placeholder="Select a Status"
                      onChange={handleStatusChange}
                      disabled={!allowToEdit}
                    >
                      <Option value="Not Completed">Not Completed</Option>
                      <Option value="Completed">Completed</Option>
                      <Option value="Cancelled">Cancelled</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Comment" name="comment">
                    <TextArea
                      rows={10}
                      onBlur={(e) => handleBlur('comment', e.target.value)}
                      disabled={!allowToEdit}
                      style={{
                        backgroundColor: '#fafafa',
                        color: 'rgba(0, 0, 0, 0.85)',
                        cursor: 'text',
                        borderColor: '#d9d9d9'
                      }} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
          {/* Right Side: Comments and Activities */}
          <div
            style={{
              width: '20%',
              paddingLeft: '16px',
              display: 'flex',
              flexDirection: 'column',
              height: '400px',
            }}
          >
            <Select value={selectedActivityType} onChange={setSelectedActivityType}>
              <Option value="both">Both</Option>
              <Option value="activities">Activities</Option>
              <Option value="comments">Comments</Option>
            </Select>
            <div
              style={{ overflowY: 'auto', flexGrow: 1, maxHeight: '300px' }}
              ref={commentsActivitiesRef}
            >
              {(selectedActivityType === 'both' || selectedActivityType === 'comments') && (
                <div style={{ marginBottom: '8px' }}>
                  <h3>Comments</h3>
                  {comments.map((commentObj, index) => (
                    <MemoizedCommentItem key={commentObj.id || index} commentObj={commentObj} />
                  ))}
                </div>
              )}
              {(selectedActivityType === 'both' || selectedActivityType === 'activities') && (
                <div style={{ marginBottom: '8px' }}>
                  <h3>Activities</h3>
                  {sortedActivities.map((activity, index) => (
                    <MemoizedActivityItem
                      key={activity.id || index}
                      activity={activity}
                      getIcon={getIcon}
                      showChangeDetails={showChangeDetails}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Comment Input */}
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
              <Input.TextArea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                style={{ flex: 1, marginRight: '8px' }}
                autoSize={{ minRows: 1, maxRows: 3 }}
                placeholder="Add a comment..."
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleAddComment}
                loading={saving}
              >
                Send
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Change Details Modal */}
      <Modal
        title="Change Details"
        visible={changeModalVisible}
        onCancel={handleCloseChangeModal}
        footer={null}
        width={800}
      >
        {changeDetails && (
          <Table
            dataSource={getChangeDetailsData(changeDetails)}
            columns={changesColumns}
            pagination={false}
          />
        )}
      </Modal>
    </>
  );
};

export default WorkstreamActionModal;